import Button from '@mui/material/Button';
import Icon from '@mui/material/Icon';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import LoginDialog from 'app/components/LoginDialog';
import TextField from '@mui/material/TextField';
import HelpIcon from '@mui/icons-material/Help';
import axios from 'app/client';
import { isValidEmail } from 'app/utils/helpers';
import * as Actions from 'app/store/actions';

// import * as authActions from 'app/auth/store/actions';
import React, { useState, useEffect } from 'react';
import { LicensingIcon, LogoutIcon } from 'app/components/Icons';

import { useSelector, useDispatch } from 'app/modules/react-redux';

import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

type Status = 'loading' | 'success' | 'error';

const UserMenu = ({ adminPage = false }: { adminPage: boolean }) => {
	const { t, i18n } = useTranslation();
	const location = useLocation();
	const dispatch = useDispatch();

	const [warningDialogOpen, setWarningDialogOpen] = useState(false);
	const [email, setEmail] = useState('');
	const [status, setStatus] = useState<Status>('loading');

	const [userMenu, setUserMenu] = useState<HTMLButtonElement | null>(null);

	const userMenuClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		setUserMenu(event.currentTarget);
	};

	const userMenuClose = () => {
		setUserMenu(null);
	};

	const handleEntraLogin = () => {
		setWarningDialogOpen(true);
	};

	const invalidForm = (): boolean => {
		const trimmedEmail = email.trim();
		return trimmedEmail === '' || !isValidEmail(trimmedEmail);
	};

	const handleEmailBlur = () => {
		setEmail(email.trim());
	};

	const getErrorMessage = (): string | undefined => {
		if (email !== '' && !isValidEmail(email.trim())) {
			return t('error text:invalid email address');
		}

		if (status === 'error') {
			return t('login:email error');
		}

		return undefined;
	};

	useEffect(() => {
		if (!warningDialogOpen) {
			setEmail('');
			setStatus('loading');
		}
	}, [warningDialogOpen]);


	const handleLogin = async () => {
		try {
			const {
				data: { ssoDomainMatched }
			} = await axios.post(`/api/sso/entra-setting`, {
				email: email.trim()
			});

			console.log('data res:', ssoDomainMatched);

			if (ssoDomainMatched === true) {
				setStatus('success');
				setWarningDialogOpen(false);
				window.location.href = `${window.location.origin}/api/sso/login/entra?email=${email}`;
			} else {
				setStatus('error');
			}
		} catch (error) {
			dispatch(Actions.handleError(error));
			setStatus('error');
		}
	};

	return (
		<>
			<Button className="h-64 px-5 text-white" onClick={userMenuClick}>
				<Icon className="text-green-accent">lock</Icon>

				<div className="mx-12">
					<Typography component="span" className="font-600 flex">
						{t('Login')}
					</Typography>
				</div>

				{/* <Icon className="text-green-accent">keyboard_arrow_down</Icon> */}
			</Button>

			<Popover
				open={!!userMenu}
				anchorEl={userMenu}
				onClose={userMenuClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'center'
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'center'
				}}
				classes={{
					paper: 'py-8'
				}}
			>
				<a
					href={`/api/sso/login?redirect=${encodeURIComponent(
						location.pathname + location.search + location.hash
					)}`}
					role="button"
				>
					<MenuItem>
						<ListItemText primary={t('login:label marketplace')} />
					</MenuItem>
				</a>
				<div onClick={handleEntraLogin} role="button" tabIndex={0}>
					<MenuItem>
						<ListItemText primary={t('login:label entra id')} />
					</MenuItem>
				</div>
			</Popover>
			<LoginDialog
				open={warningDialogOpen}
				setOpen={setWarningDialogOpen}
				content={
					<Box width={360}>
						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
							<Typography variant="h6" gutterBottom>
								{t('login:label entra id')}
							</Typography>
							<a
								href={`${process.env.REACT_APP_HELP_URL}/${i18n.language.slice(
									0,
									2
								)}/shield-guard-portal/password-vaults/#unlocking-your-password-vault`}
								target="_blank"
								rel="noreferrer noopener"
							>
								<HelpIcon color="secondary" />
							</a>
						</Box>
						<Typography className="my-20">{t('dialog:login label:email detail')}</Typography>

						<TextField
							fullWidth
							value={email}
							label={t('dialog:login label:email')}
							onChange={e => setEmail(e.target.value)}
							onBlur={handleEmailBlur}
							variant="outlined"
							required
							error={status === 'error' || (email !== '' && !isValidEmail(email.trim()))}
							inputProps={{ maxLength: 2048 }}
							helperText={getErrorMessage()}
						/>
					</Box>
				}
				action={() => {
					handleLogin();
				}}
				isValid={invalidForm()}
				buttonsText={[t('Login')]}
			/>
		</>
	);
};

export default UserMenu;
